import { doesFilterHaveValue } from '@dealstack/frontend/shared/components/Table/MantineTable/hooks/useIsFiltersApplied';
import {
	SelectionScope,
	getClampedPageIndex,
	getFinalSelectionCount,
	getTableTotalCount,
} from '@dealstack/frontend/shared/components/Table/MantineTable/table-utils';
import { MantineTableDataOperations } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableDataOperations';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { Z_INDEX } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { typedMemo } from '@dealstack/frontend/shared/other/react/typedHOC';
import { BoxProps, TextInput } from '@mantine/core';
import {
	Column,
	ColumnDef,
	ExpandedState,
	OnChangeFn,
	PaginationState,
	Row as TanstackRow,
	Table as TanstackTable,
	getCoreRowModel,
	getExpandedRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	useReactTable,
} from '@tanstack/react-table';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BulkActionGroup } from './ActionsButton/ActionsButton.types';
import { AppliedFiltersSubrow } from './AppliedFiltersSubrow';
import { useTableGridLayout } from './hooks/useTableGridLayout';
import { LEADING_TRACK_WIDTH } from './utils/pinnedColumn';
import { MantineTableBody } from './MantineTableBody';
import { MantineTableFilterRow } from './MantineTableFilterRow';
import { MantineTableFilterSubrow } from './MantineTableFilterSubrow';
import { MantineTableHeader } from './MantineTableHeader';
import { MantineTablePagination } from './MantineTablePagination';
import { MantineTopToolbar } from './MantineTopToolbar/MantineTopToolbar';
import {
	CellPropsRenderer,
	ContainerPropsRenderer,
	DetailPanelRenderer,
	DragDropHandler,
	RowActionRenderer,
	RowPropsRenderer,
	RowReorderHandler,
	TableData,
	getTableMeta,
} from './types';

interface Props<T extends TableData> {
	columns: ExtendedColumnDef<T, unknown>[];
	data: T[];
	dataOperations?: MantineTableDataOperations;
	tableRef?: React.LegacyRef<HTMLDivElement>;
	isLoading?: boolean;
	/**
	 * Enable manual pagination mode.
	 *
	 * @remarks
	 * **IMPORTANT**: When enabled, the selection state will be inverted.
	 * This means that you will only know what is NOT selected, not what is selected.
	 *
	 *
	 * When `true`, the table will not automatically paginate data. Instead, you must:
	 * - Pass the current page's data via the `data` prop
	 * - Provide total row count via `rowCount` prop
	 * - Handle pagination changes via `onPaginationChange` callback
	 * - Manage pagination state externally via `state.pagination`
	 *
	 * When `false`, the table handles pagination internally using all data passed to `data` prop.
	 *
	 * @default true
	 *
	 * @example
	 * ```tsx
	 * // Manual pagination (server-side)
	 * <MantineTable
	 *   data={currentPageData}
	 *   manualPagination={true}
	 *   rowCount={totalCount}
	 *   state={{ pagination: { pageIndex: 0, pageSize: 10 } }}
	 *   onPaginationChange={handlePageChange}
	 * />
	 *
	 * // Auto pagination (client-side)
	 * <MantineTable
	 *   data={allData}
	 *   manualPagination={false}
	 * />
	 * ```
	 */
	manualPagination?: boolean;
	manualFiltering?: boolean;
	manualSorting?: boolean;
	enableRowSelection?: boolean | ((row: T) => boolean);
	enableSearchBar?: boolean;
	renderTopToolbar?:
		| ((params: { table: TanstackTable<T> }) => React.ReactNode)
		| null
		| React.ReactNode;
	renderBottomToolbarCustomActions?: boolean;
	mantineTableBodyRowProps?: RowPropsRenderer<T>;
	getRowId?: (row: T, index: number, parent?: unknown) => string;
	actions?: (table: TanstackTable<T>) => BulkActionGroup<T>;
	renderBulkActionsMenu?: boolean;
	customSettingsRenderer?: (
		table: TanstackTable<T>,
		allColumns?: Column<T, unknown>[]
	) => JSX.Element;
	customFilters?: JSX.Element[];
	appliedFilterLabels?: Record<string, string>;
	quickFilterOptions?: Array<{
		value: string;
		label: string;
		filterKey?: string;
		filterValue?: boolean | string;
	}>;
	showDeletedTable?: boolean;
	returnToTable?: () => void;
	mantineTableContainerProps?: ContainerPropsRenderer<T>;
	mantineTableBodyProps?: ContainerPropsRenderer<T>;
	mantineTableBodyCellProps?: CellPropsRenderer<T>;
	rowCount?: number;
	getSubRows?: (originalRow: T, index?: number) => T[] | undefined;
	state?: Partial<{
		pagination: PaginationState;
		columnVisibility: Record<string, boolean>;
		rowSelection: Record<string, boolean>;
		columnSizing: Record<string, number>;
	}>;
	onPaginationChange?: OnChangeFn<PaginationState>;
	onRowSelectionChange?: OnChangeFn<Record<string, boolean>>;
	onColumnVisibilityChange?: OnChangeFn<Record<string, boolean>>;
	onIsFullScreenChange?: (value: boolean) => void;
	showColumnFilters?: boolean;
	onShowColumnFiltersChange?: (value: boolean) => void;
	initialState?: Partial<{
		columnVisibility: Record<string, boolean>;
		showColumnFilters: boolean;
	}>;
	enableRowActions?: boolean;
	enableStickyHeader?: boolean;
	enableStickyFooter?: boolean;
	enableTopToolbar?: boolean;
	enableExpanding?: boolean;
	enableExpandAll?: boolean;
	grouped?: boolean;
	icons?: Record<string, unknown>;
	renderRowActions?: RowActionRenderer<T>;
	renderRowActionMenuItems?: RowActionRenderer<T>;
	renderEmptyRowsFallback?: () => React.ReactNode;
	renderDetailPanel?: DetailPanelRenderer<T>;
	mantineDetailPanelProps?: React.HTMLProps<HTMLDivElement> & BoxProps;
	getRowCanExpand?: (row: TanstackRow<T>) => boolean;
	enablePagination?: boolean;
	enableRenderBottomToolbarCustomActions?: boolean;
	enableFilters?: boolean;
	enableGlobalFilter?: boolean;
	enableFullScreenToggle?: boolean;
	enableHiding?: boolean;
	minWidth?: number;
	hideBorders?: boolean;
	hideFooterRows?: boolean;
	enableRowDragAndDrop?: boolean;
	enableColumnResizing?: boolean;
	/**
	 * Column ids to freeze so they stay in place while the table is scrolled
	 * horizontally. Defaults to none (no pinning). e.g. `['actions', 'investor']`
	 * freezes a leading expander/actions column alongside the investor column.
	 *
	 * Pins by id (not position), so a hidden pinned column simply drops out of
	 * the frozen set rather than leaking the pin onto the next visible column.
	 * When row selection is active the checkbox column is frozen too.
	 */
	pinnedColumnIds?: string[];
	onRowReorder?: RowReorderHandler<T>;
	onRowDropped?: DragDropHandler<T>;
	dataTestId?: string;
	onActionCanceled?: () => void;
	/**
	 * Optional imperative handle so a parent can programmatically reset the action-state
	 * (e.g. to exit a custom "delete mode" via a banner Cancel button rather than the
	 * built-in close icon).
	 */
	actionResetRef?: React.MutableRefObject<(() => void) | null>;
	enableVerticalCellBorders?: boolean;
	footerCellMinHeight?: string;
	footerCellAlignTop?: boolean;
}

const MantineTable = <T extends TableData>({
	columns,
	data,
	dataOperations,
	tableRef,
	isLoading,
	manualPagination = false,
	manualFiltering = true,
	manualSorting = true,
	enableRowSelection = true,
	enableSearchBar = true,
	enableRowActions = true,
	renderTopToolbar,
	mantineTableBodyRowProps,
	getRowId,
	actions,
	renderBulkActionsMenu,
	customSettingsRenderer,
	customFilters,
	appliedFilterLabels,
	quickFilterOptions,
	showDeletedTable,
	returnToTable,
	mantineTableContainerProps,
	mantineTableBodyCellProps,
	rowCount,
	getSubRows,
	state: externalState,
	onPaginationChange: externalOnPaginationChange,
	onRowSelectionChange: externalOnRowSelectionChange,
	onColumnVisibilityChange: externalOnColumnVisibilityChange,
	onIsFullScreenChange,
	showColumnFilters: controlledShowColumnFilters,
	onShowColumnFiltersChange,
	initialState,
	renderRowActions,
	renderRowActionMenuItems,
	renderEmptyRowsFallback,
	enableStickyHeader = true,
	enableStickyFooter = true,
	enableTopToolbar = true,
	enableHiding = true,
	enableFullScreenToggle = true,
	enablePagination = true,
	minWidth = 160,
	enableExpanding = false,
	renderDetailPanel,
	mantineDetailPanelProps,
	getRowCanExpand,
	hideBorders = false,
	hideFooterRows = false,
	enableRowDragAndDrop = false,
	enableColumnResizing = false,
	pinnedColumnIds = [],
	onRowReorder,
	onRowDropped,
	dataTestId,
	onActionCanceled,
	actionResetRef,
	enableVerticalCellBorders = false,
	footerCellMinHeight,
	footerCellAlignTop = false,
}: Props<T>) => {
	const hasRowSelectionColumn = !!enableRowSelection;
	// When a table opts into column pinning, the selection checkbox is frozen
	// too so it stays visible while scrolling horizontally.
	const pinSelectionColumn = pinnedColumnIds.length > 0;
	const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
	const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(
		initialState?.columnVisibility ?? {}
	);

	const [ownShowColumnFilters, setOwnShowColumnFilters] = useState(
		initialState?.showColumnFilters ?? false
	);
	const showColumnFilters = controlledShowColumnFilters ?? ownShowColumnFilters;
	const setShowColumnFilters = onShowColumnFiltersChange ?? setOwnShowColumnFilters;
	const [showGlobalFilter, setShowGlobalFilter] = useState(false);
	const [isFullScreen, setIsFullScreen] = useState(false);
	const [selectAll, setSelectAll] = useState(false);
	const [greedySelecting, setGreedySelecting] = useState(false);
	const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);
	const [showRowSelection, setShowRowSelection] = useState(false);
	const [actionsMode, setActionsMode] = useState<'default' | 'select' | 'action'>('default');
	const [expanded, setExpanded] = useState<ExpandedState>({});
	const [showSelectionHeader, setShowSelectionHeader] = useState(false);
	const resetActionStateRef = useRef<(() => void) | null>(null);

	const [localData, setLocalData] = useState<T[]>(data);

	useEffect(() => {
		setLocalData(data);
	}, [data]);

	const handleToggleFullScreen = useCallback(
		(value: boolean) => {
			setIsFullScreen(value);
			onIsFullScreenChange?.(value);
		},
		[onIsFullScreenChange]
	);

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape' && isFullScreen) {
				handleToggleFullScreen(false);
			}
		};

		if (isFullScreen) {
			document.addEventListener('keydown', handleKeyDown);
		}

		return () => {
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [isFullScreen, handleToggleFullScreen]);

	const [selectionScope, setSelectionScope] = useState(SelectionScope.Page);

	useEffect(() => {
		if (externalState?.columnVisibility) {
			setColumnVisibility(externalState.columnVisibility);
		}
	}, [externalState?.columnVisibility]);

	// Track filter state to clear selection when filters change
	const isInitialMount = useRef(true);
	const prevColumnFilters = useRef(dataOperations?.columnFilters);
	const prevGlobalSearchString = useRef(dataOperations?.globalSearchString);

	// Clear selection when filters change
	useEffect(() => {
		if (isInitialMount.current) {
			isInitialMount.current = false;
			prevColumnFilters.current = dataOperations?.columnFilters;
			prevGlobalSearchString.current = dataOperations?.globalSearchString;
			return;
		}

		const columnFiltersChanged =
			JSON.stringify(prevColumnFilters.current) !==
			JSON.stringify(dataOperations?.columnFilters);
		const globalSearchChanged =
			prevGlobalSearchString.current !== dataOperations?.globalSearchString;

		if (columnFiltersChanged || globalSearchChanged) {
			// Clear all selection-related state
			setSelectAll(false);
			setGreedySelecting(false);
			setSelectionScope(SelectionScope.Page);

			// Clear row selection (handle both internal and external state)
			if (externalOnRowSelectionChange) {
				externalOnRowSelectionChange({});
			} else {
				setRowSelection({});
			}

			// Update refs
			prevColumnFilters.current = dataOperations?.columnFilters;
			prevGlobalSearchString.current = dataOperations?.globalSearchString;
		}
	}, [
		dataOperations?.columnFilters,
		dataOperations?.globalSearchString,
		externalOnRowSelectionChange,
	]);

	const setDataOperationsPagination = dataOperations?.setPagination;
	const onPaginationChange: OnChangeFn<PaginationState> | undefined = useMemo(
		() =>
			externalOnPaginationChange ??
			(setDataOperationsPagination
				? (updater) => setDataOperationsPagination(updater as PaginationState)
				: undefined),
		[externalOnPaginationChange, setDataOperationsPagination]
	);

	const currentPagination = externalState?.pagination ?? dataOperations?.pagination;

	useEffect(() => {
		const isExternallyOwned = externalState?.pagination != null && !externalOnPaginationChange;

		if (
			!enablePagination ||
			!manualPagination ||
			isLoading ||
			// An empty set renders empty on every page, so clamping only costs the user
			// their position and the pageIndex url param.
			!rowCount ||
			isExternallyOwned ||
			!currentPagination
		) {
			return;
		}

		const clampedPageIndex = getClampedPageIndex({
			rowCount,
			pageIndex: currentPagination.pageIndex,
			pageSize: currentPagination.pageSize,
		});

		if (clampedPageIndex !== currentPagination.pageIndex) {
			onPaginationChange?.({ ...currentPagination, pageIndex: clampedPageIndex });
		}
	}, [
		enablePagination,
		manualPagination,
		isLoading,
		rowCount,
		currentPagination,
		onPaginationChange,
		externalState?.pagination,
		externalOnPaginationChange,
	]);

	const onColumnVisibilityChange: OnChangeFn<Record<string, boolean>> | undefined =
		externalOnColumnVisibilityChange ?? setColumnVisibility;

	const [columnSizing, setColumnSizing] = useState<Record<string, number>>({});

	const table = useReactTable<T>({
		data: enableRowDragAndDrop ? localData : data ?? [],
		columns: columns as ColumnDef<T, unknown>[],
		state: {
			columnFilters: dataOperations?.columnFilters ?? [],
			globalFilter: dataOperations?.globalSearchString,
			pagination: externalState?.pagination ?? dataOperations?.pagination,
			sorting: dataOperations?.sorting ?? [],
			rowSelection: externalState?.rowSelection ?? rowSelection,
			columnVisibility: externalState?.columnVisibility ?? columnVisibility,
			columnSizing: externalState?.columnSizing ?? columnSizing,
			expanded,
		},
		defaultColumn: {
			size: undefined,
			minSize: minWidth,
		},
		getRowId,
		manualPagination: enablePagination ? manualPagination : false,
		manualFiltering,
		manualSorting,
		onColumnFiltersChange: dataOperations?.setColumnFilters,
		onGlobalFilterChange: dataOperations?.setGlobalSearchString,
		onPaginationChange,
		onSortingChange: dataOperations?.setSorting,
		onRowSelectionChange: externalOnRowSelectionChange ?? setRowSelection,
		onColumnVisibilityChange,
		onColumnSizingChange: setColumnSizing,
		onExpandedChange: setExpanded,
		getCoreRowModel: getCoreRowModel(),
		getFilteredRowModel: manualFiltering ? undefined : getFilteredRowModel(),
		getSortedRowModel: manualSorting ? undefined : getSortedRowModel(),
		getPaginationRowModel: (() => {
			if (!enablePagination) {
				return undefined;
			}
			return manualPagination ? undefined : getPaginationRowModel();
		})(),
		getExpandedRowModel: enableExpanding ? getExpandedRowModel() : undefined,
		getSubRows,
		enableColumnResizing,
		columnResizeMode: enableColumnResizing ? ('onChange' as const) : undefined,
		enableRowSelection:
			typeof enableRowSelection === 'function'
				? (row) => enableRowSelection(row.original)
				: enableRowSelection,
		enableHiding,
		getRowCanExpand,
		meta: {
			selectAll,
			setSelectAll,
			greedySelecting,
			setGreedySelecting,
			selectionScope,
			setSelectionScope,
			showGlobalFilter,
			setShowGlobalFilter,
			showColumnFilters,
			setShowColumnFilters,
			isFullScreen,
			setIsFullScreen: handleToggleFullScreen,
			enablePagination,
			manualPagination,
			dataTestId,
			resetActionState: () => resetActionStateRef.current?.(),
		},
		...(rowCount != null ? { rowCount } : {}),
	});

	const hasAppliedColumnFilters = table
		.getState()
		.columnFilters.some((filter) => doesFilterHaveValue(filter.value));
	const hasAppliedGlobalFilter = doesFilterHaveValue(table.getState().globalFilter);
	const hasAppliedFilters = hasAppliedColumnFilters || hasAppliedGlobalFilter;

	const gridLayout = useTableGridLayout({
		table,
		enableRowDragAndDrop,
		enableRowSelection: hasRowSelectionColumn,
		showRowSelection,
		enableRowActions,
		renderRowActions,
		renderRowActionMenuItems,
		minWidth,
		columnSizing: enableColumnResizing ? table.getState().columnSizing : undefined,
	});

	if (table.options.meta) {
		getTableMeta(table).gridLayout = gridLayout;

		const pinnedColumns: Record<string, number> = {};
		let lastPinnedColumnId: string | undefined;

		if (pinnedColumnIds.length > 0) {
			// Pinning is by ID. Only the listed columns are frozen, so hiding one
			// drops it from the frozen set instead of promoting the next visible
			// column (id-based, not positional).
			const pinnedIdSet = new Set(pinnedColumnIds);

			// The checkbox (and drag handle) render as leading fixed tracks
			// before the data columns and are frozen alongside them, so the data
			// columns' offsets start after those tracks. (Drag + pinning isn't a
			// combination any current table uses.)
			let leftOffset =
				(enableRowDragAndDrop ? LEADING_TRACK_WIDTH : 0) +
				(hasRowSelectionColumn && showRowSelection ? LEADING_TRACK_WIDTH : 0);

			// Walk in visible order so offsets follow the rendered layout.
			for (const column of table.getVisibleLeafColumns()) {
				if (!pinnedIdSet.has(column.id)) {
					continue;
				}
				pinnedColumns[column.id] = leftOffset;
				// getSize() is the column's definition/resize width. For the
				// grid's flexible tracks (minmax(minPx, fr)) this equals the
				// rendered width only while the table overflows horizontally —
				// which is exactly when pinning is visible. Pinning >1 column is
				// therefore only reliable when the leading pinned columns are
				// effectively fixed-width.
				leftOffset += column.getSize();
				lastPinnedColumnId = column.id;
			}
		}

		getTableMeta(table).pinnedColumns = pinnedColumns;
		getTableMeta(table).lastPinnedColumnId = lastPinnedColumnId;
	}

	const selectedCount = getFinalSelectionCount(table);
	const totalCount = getTableTotalCount(table);

	// Auto-transition between 'select' and 'action' modes based on selection
	useEffect(() => {
		const isGreedyAction = manualPagination && greedySelecting && selectedCount < totalCount;

		if ((actionsMode === 'select' && selectedCount > 0) || isGreedyAction) {
			setActionsMode('action');
		} else if (actionsMode === 'action' && selectedCount === 0) {
			setActionsMode('select');
		}
		setShowSelectionHeader(actionsMode !== 'default');
	}, [table, actionsMode, greedySelecting, manualPagination, selectedCount, totalCount]);

	const handleActionCanceled = useCallback(() => {
		const { setGreedySelecting, setSelectionScope, setSelectAll } = getTableMeta(table);

		setGreedySelecting(false);
		setSelectionScope(SelectionScope.Page);
		setSelectAll(false);
		table.setRowSelection({});
		onActionCanceled?.();
	}, [table, onActionCanceled]);

	const resetActionState = useCallback(() => {
		setActionsMode('default');
		setShowRowSelection(false);
		setShowSelectionHeader(false);
		handleActionCanceled();
	}, [handleActionCanceled]);

	useEffect(() => {
		resetActionStateRef.current = resetActionState;
		if (actionResetRef) {
			actionResetRef.current = resetActionState;
		}
	}, [resetActionState, actionResetRef]);

	const renderHeaderContent = () => {
		if (!enableTopToolbar) {
			return null;
		}

		if (renderTopToolbar) {
			if (typeof renderTopToolbar === 'function') {
				return renderTopToolbar({ table });
			}
			return renderTopToolbar;
		}
		if (enableTopToolbar || renderBulkActionsMenu || customSettingsRenderer || customFilters) {
			return (
				<MantineTopToolbar<T>
					table={table}
					actions={actions}
					renderBulkActionsMenu={renderBulkActionsMenu}
					customSettingsRenderer={customSettingsRenderer}
					customFilters={customFilters}
					quickFilterOptions={quickFilterOptions}
					quickFilters={dataOperations?.quickFilters}
					onQuickFiltersChange={dataOperations?.setQuickFilters}
					showDeletedTable={showDeletedTable}
					returnToTable={returnToTable}
					enableSearchBar={enableSearchBar}
					enableFullScreenToggle={enableFullScreenToggle}
					actionsMode={actionsMode}
					onActionsModeChange={setActionsMode}
					showRowSelection={showRowSelection}
					onShowRowSelectionChange={setShowRowSelection}
					onActionCanceled={handleActionCanceled}
					showSelectionHeader={showSelectionHeader}
				/>
			);
		}
		if (!enableSearchBar) {
			return null;
		}
		return (
			<div
				style={{
					display: 'flex',
					padding: '8px',
					border: `1px solid ${defaultTheme.v3.colors.gray[4]}`,
					borderRadius: 4,
					marginBottom: 8,
				}}
			>
				<TextInput
					placeholder="Search"
					value={(table.getState().globalFilter as string) ?? ''}
					onChange={(e) => table.setGlobalFilter(e.currentTarget.value ?? '')}
					style={{ flex: 1 }}
				/>
			</div>
		);
	};

	const resolvedContainerProps =
		typeof mantineTableContainerProps === 'function'
			? mantineTableContainerProps({ table })
			: mantineTableContainerProps;

	const headerContent = renderHeaderContent();

	return (
		<div
			ref={tableRef}
			className="mantine-table-container"
			style={{
				position: isFullScreen ? 'fixed' : 'relative',
				top: isFullScreen ? 0 : 'auto',
				left: isFullScreen ? 0 : 'auto',
				right: isFullScreen ? 0 : 'auto',
				bottom: isFullScreen ? 0 : 'auto',
				height: isFullScreen ? '100vh' : '100%',
				width: isFullScreen ? '100vw' : '100%',
				display: 'flex',
				flexDirection: 'column',
				backgroundColor: 'white',
				zIndex: isFullScreen ? Z_INDEX.FULLSCREEN_TABLE : 'auto',
				transition: 'all 0.1s ease-in',
				overflow: isFullScreen ? 'hidden' : 'visible',
			}}
			{...resolvedContainerProps}
			data-testid={dataTestId}
		>
			{headerContent}
			<div
				className="mantine-table-scroll-area"
				style={{
					overflow: 'auto',
					flex: '0 1 auto',
					minHeight: 0,
					maxHeight: '100%',
					position: 'relative',
					...(headerContent
						? {
								borderBottom: `1px solid ${defaultTheme.v3.colors.gray[4]}`,
								borderLeft: `1px solid ${defaultTheme.v3.colors.gray[4]}`,
								borderRight: `1px solid ${defaultTheme.v3.colors.gray[4]}`,
								borderBottomLeftRadius: '0.25rem',
								borderBottomRightRadius: '0.25rem',
							}
						: {
								border: hideBorders
									? 'none'
									: `1px solid ${defaultTheme.v3.colors.gray[4]}`,
								borderRadius: hideBorders ? '0' : '0.25rem',
							}),
				}}
			>
				<div
					style={{
						display: 'grid',
						gridTemplateColumns: gridLayout.gridTemplateColumns,
						minWidth: '100%',
					}}
					role="table"
				>
					<MantineTableHeader
						table={table}
						gridLayout={gridLayout}
						enableRowDragAndDrop={enableRowDragAndDrop}
						enableRowSelection={hasRowSelectionColumn}
						showRowSelection={showRowSelection}
						pinSelectionColumn={pinSelectionColumn}
						enableRowActions={enableRowActions}
						renderRowActions={renderRowActions}
						renderRowActionMenuItems={renderRowActionMenuItems}
						enableStickyHeader={enableStickyHeader}
						enableColumnResizing={enableColumnResizing}
						selectionScope={selectionScope}
						setSelectionScope={setSelectionScope}
						enableVerticalCellBorders={enableVerticalCellBorders}
					/>

					<MantineTableFilterRow
						table={table}
						enableRowDragAndDrop={enableRowDragAndDrop}
						enableRowSelection={hasRowSelectionColumn}
						showRowSelection={showRowSelection}
						pinSelectionColumn={pinSelectionColumn}
						enableRowActions={enableRowActions}
						renderRowActions={renderRowActions}
						renderRowActionMenuItems={renderRowActionMenuItems}
						minWidth={minWidth}
						enableStickyHeader={enableStickyHeader}
					/>

					<MantineTableFilterSubrow
						table={table}
						enableStickyHeader={enableStickyHeader}
						isVisible={hasAppliedFilters}
					>
						<AppliedFiltersSubrow
							table={table}
							appliedFilterLabels={appliedFilterLabels}
						/>
					</MantineTableFilterSubrow>

					<MantineTableBody
						table={table}
						gridLayout={gridLayout}
						enableRowDragAndDrop={enableRowDragAndDrop}
						enableRowSelection={hasRowSelectionColumn}
						showRowSelection={showRowSelection}
						pinSelectionColumn={pinSelectionColumn}
						enableRowActions={enableRowActions}
						renderRowActions={renderRowActions}
						renderRowActionMenuItems={renderRowActionMenuItems}
						enableExpanding={enableExpanding}
						renderDetailPanel={renderDetailPanel}
						mantineDetailPanelProps={mantineDetailPanelProps}
						mantineTableBodyRowProps={mantineTableBodyRowProps}
						mantineTableBodyCellProps={mantineTableBodyCellProps}
						getRowId={getRowId}
						hoveredRowId={hoveredRowId}
						setHoveredRowId={setHoveredRowId}
						hideFooterRows={hideFooterRows}
						enableStickyFooter={enableStickyFooter}
						isLoading={isLoading}
						renderEmptyRowsFallback={renderEmptyRowsFallback}
						onRowReorder={onRowReorder}
						onRowDropped={onRowDropped}
						localData={localData}
						setLocalData={setLocalData}
						enableVerticalCellBorders={enableVerticalCellBorders}
						footerCellMinHeight={footerCellMinHeight}
						footerCellAlignTop={footerCellAlignTop}
					/>
				</div>
			</div>
			<MantineTablePagination
				enablePagination={enablePagination}
				dataOperations={dataOperations}
				rowCount={rowCount ?? table.getPrePaginationRowModel().rows.length}
			/>
		</div>
	);
};

const Memoized = typedMemo(MantineTable);

export { Memoized as MantineTable };
